import React, { useState } from 'react';
import { UserCheck, Star, MessageCircle, Video, Phone, MapPin, Filter, Search, Clock, Award, Shield, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import CoachProfileModal from '../components/CoachProfileModal';
import BookSessionModal from '../components/BookSessionModal';
import { useToast } from '../hooks/use-toast';

const LifeCoaches = () => {
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCoachProfile, setShowCoachProfile] = useState(false);
  const [showBookSession, setShowBookSession] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const { toast } = useToast();

  const specialties = [
    { id: 'all', name: 'All Specialties', count: 47 },
    { id: 'self-love', name: 'Self-Love & Acceptance', count: 12 },
    { id: 'confidence', name: 'Confidence Building', count: 15 },
    { id: 'relationships', name: 'Relationships', count: 8 },
    { id: 'career', name: 'Career & Purpose', count: 9 },
    { id: 'wellness', name: 'Wellness & Mindfulness', count: 11 },
    { id: 'anxiety', name: 'Anxiety & Stress', count: 7 }
  ];

  const sessionFormats = [
    { id: 'all', name: 'All Formats' },
    { id: 'video', name: 'Video Call', icon: Video },
    { id: 'phone', name: 'Phone Call', icon: Phone },
    { id: 'messaging', name: 'Messaging', icon: MessageCircle },
    { id: 'in-person', name: 'In-Person', icon: MapPin }
  ];

  const coaches = [
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      title: 'Licensed Clinical Psychologist & Self-Love Specialist',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face',
      rating: 4.9,
      reviews: 234,
      hourlyRate: 120,
      location: 'San Francisco, CA',
      verified: true,
      topRated: true,
      responseTime: '< 1 hour',
      completedSessions: 890,
      specialties: ['self-love', 'confidence', 'anxiety'],
      formats: ['video', 'phone', 'messaging'],
      languages: ['English', 'Spanish'],
      description: 'Transform your relationship with yourself through evidence-based techniques and compassionate guidance. Specializing in self-love, confidence building, and overcoming limiting beliefs.',
      achievements: ['PhD Clinical Psychology', '10+ Years Experience', 'Featured in Forbes'],
      communityRank: 1,
      communityBadge: 'Love Champion'
    },
    {
      id: 2,
      name: 'Emma Rodriguez',
      title: 'Certified Life Coach & Wellness Expert',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b789?w=150&h=150&fit=crop&crop=face',
      rating: 4.8,
      reviews: 189,
      hourlyRate: 85,
      location: 'Austin, TX',
      verified: true,
      topRated: false,
      responseTime: '< 2 hours',
      completedSessions: 567,
      specialties: ['wellness', 'relationships', 'career'],
      formats: ['video', 'messaging', 'in-person'],
      languages: ['English'],
      description: 'Empowering women to create authentic, fulfilling lives through mindful practices and strategic goal-setting. Let\'s unlock your potential together!',
      achievements: ['ICF Certified Coach', 'Mindfulness Instructor', '500+ Happy Clients'],
      communityRank: 3,
      communityBadge: 'Growth Warrior'
    },
    {
      id: 3,
      name: 'Marcus Thompson',
      title: 'Executive Coach & Confidence Mentor',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      rating: 4.7,
      reviews: 156,
      hourlyRate: 150,
      location: 'New York, NY',
      verified: true,
      topRated: true,
      responseTime: '< 30 minutes',
      completedSessions: 423,
      specialties: ['confidence', 'career', 'self-love'],
      formats: ['video', 'phone'],
      languages: ['English', 'French'],
      description: 'Former Fortune 500 executive turned coach. Helping professionals and entrepreneurs build unshakeable confidence and achieve breakthrough results.',
      achievements: ['MBA Harvard', '15+ Years Corporate', 'Tony Robbins Certified'],
      communityRank: 5,
      communityBadge: 'Success Mentor'
    },
    {
      id: 4,
      name: 'Dr. Lisa Chen',
      title: 'Relationship Therapist & Self-Compassion Expert',
      avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face',
      rating: 4.9,
      reviews: 298,
      hourlyRate: 110,
      location: 'Los Angeles, CA',
      verified: true,
      topRated: true,
      responseTime: '< 1 hour',
      completedSessions: 712,
      specialties: ['relationships', 'self-love', 'anxiety'],
      formats: ['video', 'messaging'],
      languages: ['English', 'Mandarin'],
      description: 'Helping individuals develop deeper self-compassion and healthier relationships. Specializing in attachment styles and emotional intelligence.',
      achievements: ['PhD Marriage & Family', 'Published Author', 'TEDx Speaker'],
      communityRank: 2,
      communityBadge: 'Love Guardian'
    },
    {
      id: 5,
      name: 'James Wilson',
      title: 'Mindfulness Coach & Anxiety Specialist',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      rating: 4.6,
      reviews: 127,
      hourlyRate: 75,
      location: 'Denver, CO',
      verified: true,
      topRated: false,
      responseTime: '< 3 hours',
      completedSessions: 345,
      specialties: ['wellness', 'anxiety', 'confidence'],
      formats: ['video', 'phone', 'messaging', 'in-person'],
      languages: ['English'],
      description: 'Former anxiety sufferer turned mindfulness expert. Using meditation, breathwork, and cognitive techniques to help you find inner peace and confidence.',
      achievements: ['200hr Yoga Teacher', 'Mindfulness Based Stress Reduction', '5+ Years Teaching'],
      communityRank: 8,
      communityBadge: 'Zen Master'
    }
  ];

  const filteredCoaches = coaches.filter(coach => {
    const matchesSpecialty = selectedSpecialty === 'all' || coach.specialties.includes(selectedSpecialty);
    const matchesFormat = selectedFormat === 'all' || coach.formats.includes(selectedFormat);
    const matchesSearch = searchQuery === '' || 
      coach.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coach.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSpecialty && matchesFormat && matchesSearch;
  });

  const handleViewProfile = (coach) => {
    setSelectedCoach(coach);
    setShowCoachProfile(true);
  };

  const handleBookSession = (coach) => {
    setSelectedCoach(coach);
    setShowBookSession(true);
  };

  const handleMessage = (coach) => {
    toast({
      title: "📩 Message Sent!",
      description: `Your message was sent to ${coach.name}. They'll respond within ${coach.responseTime}.`,
      duration: 5000,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <UserCheck className="w-8 h-8 text-rose-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">Life Coaches Marketplace</h1>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Connect with verified life coaches who specialize in self-love, confidence, and personal growth. All sessions are conducted safely within our platform.
          </p>
        </div>

        {/* Trust Indicators */}
        <div className="flex items-center justify-center space-x-8 mb-8 text-sm text-gray-500">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Verified Professionals</span>
          </div>
          <div className="flex items-center space-x-2">
            <Heart className="w-4 h-4 text-rose-500" />
            <span>Platform-Secure Payments</span>
          </div>
          <div className="flex items-center space-x-2">
            <Award className="w-4 h-4 text-amber-500" />
            <span>Quality Guaranteed</span>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-6 mb-8 bg-white/70 backdrop-blur-sm border-rose-200">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Coaches</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Name, specialty, or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-rose-200 focus:ring-rose-300 focus:border-rose-300"
                />
              </div>
            </div>

            {/* Specialty Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
              <select
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                className="w-full p-3 border border-rose-200 rounded-lg focus:ring-rose-300 focus:border-rose-300"
              >
                {specialties.map((specialty) => (
                  <option key={specialty.id} value={specialty.id}>
                    {specialty.name} ({specialty.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Format Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Format</label>
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="w-full p-3 border border-rose-200 rounded-lg focus:ring-rose-300 focus:border-rose-300"
              >
                {sessionFormats.map((format) => (
                  <option key={format.id} value={format.id}>
                    {format.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {filteredCoaches.length} coaches available
          </h2>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
            <span>Sorted by relevance</span>
          </div>
        </div>

        {/* Coaches Grid */}
        <div className="grid gap-6">
          {filteredCoaches.map((coach) => (
            <Card key={coach.id} className="p-6 bg-white/70 backdrop-blur-sm border-rose-200 hover:shadow-xl transition-all duration-300">
              <div className="flex flex-col lg:flex-row lg:items-start space-y-4 lg:space-y-0 lg:space-x-6">
                
                {/* Coach Avatar & Basic Info */}
                <div className="flex items-start space-x-4 lg:flex-shrink-0">
                  <div className="relative">
                    <img 
                      src={coach.avatar} 
                      alt={coach.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                    {coach.verified && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Shield className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-xl font-bold text-gray-800">{coach.name}</h3>
                      {coach.topRated && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">
                          ⭐ Top Rated
                        </Badge>
                      )}
                    </div>
                    <p className="text-rose-600 font-medium mb-2">{coach.title}</p>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 mr-1" />
                        <span className="font-medium">{coach.rating}</span>
                        <span className="text-gray-400 ml-1">({coach.reviews} reviews)</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                        <span>{coach.location}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>Responds in {coach.responseTime}</span>
                      </div>
                      <span>•</span>
                      <span>{coach.completedSessions} sessions completed</span>
                    </div>
                  </div>
                </div>

                {/* Coach Details */}
                <div className="flex-1">
                  <p className="text-gray-700 mb-4 leading-relaxed">{coach.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Specialties:</h4>
                      <div className="flex flex-wrap gap-2">
                        {coach.specialties.map((specialty) => (
                          <Badge key={specialty} variant="outline" className="text-xs border-rose-300 text-rose-700">
                            {specialties.find(s => s.id === specialty)?.name.replace(' & ', ' & ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-800 mb-2">Session Formats:</h4>
                      <div className="flex flex-wrap gap-2">
                        {coach.formats.map((format) => {
                          const formatData = sessionFormats.find(f => f.id === format);
                          const Icon = formatData?.icon;
                          return (
                            <div key={format} className="flex items-center space-x-1 text-xs text-gray-600">
                              {Icon && <Icon className="w-3 h-3" />}
                              <span>{formatData?.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-rose-100">
                    <div className="flex items-center space-x-4">
                      <div className="text-2xl font-bold text-gray-800">
                        ${coach.hourlyRate}/hr
                      </div>
                      {coach.communityBadge && (
                        <Badge className="bg-rose-100 text-rose-800 text-xs">
                          🏆 {coach.communityBadge}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Button
                        onClick={() => handleMessage(coach)}
                        variant="outline"
                        size="sm"
                        className="border-rose-300 text-rose-600 hover:bg-rose-50"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message
                      </Button>
                      
                      <Button
                        onClick={() => handleViewProfile(coach)}
                        variant="outline"
                        size="sm"
                        className="border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        View Profile
                      </Button>
                      
                      <Button
                        onClick={() => handleBookSession(coach)}
                        className="bg-rose-500 hover:bg-rose-600 text-white"
                      >
                        Book Session
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <Card className="mt-12 p-8 text-center bg-white/70 backdrop-blur-sm border-rose-200">
          <UserCheck className="w-16 h-16 text-rose-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            Are you a certified life coach?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join our exclusive marketplace and connect with clients who are ready to invest in their self-love journey. Verified professionals only.
          </p>
          <Button className="bg-rose-500 hover:bg-rose-600 text-white px-8 py-3">
            Apply to Become a Coach
          </Button>
        </Card>
      </div>

      <CoachProfileModal
        isOpen={showCoachProfile}
        onClose={() => setShowCoachProfile(false)}
        coach={selectedCoach}
        onBookSession={() => {
          setShowCoachProfile(false);
          setShowBookSession(true);
        }}
        onMessage={() => handleMessage(selectedCoach)}
      />

      <BookSessionModal
        isOpen={showBookSession}
        onClose={() => setShowBookSession(false)}
        coach={selectedCoach}
      />
    </div>
  );
};

export default LifeCoaches;